import { Controller } from "@hotwired/stimulus"

// Fills the market step's three fields from the website read on step one (B15).
//
// Night 23 gave this controller its own address field, its own button and its
// own AI call, not knowing that step one already had a Website field that
// analysed the site on blur. Night 25 removed the second field: the read happens
// once, where the address is already typed, and its three suggestions arrive
// here as an event from onboarding--company-analysis.
//
// The fields are the SAME elements the wizard already reads at save time, so
// nothing new is persisted and no new save path exists: this writes into the
// boxes, and the person's own Next/Generate does the saving, once they have
// read what is in them.
//
// Every route still ends at a visible sentence in statusTarget, including the
// one where the call succeeded and fitted nothing. There is no branch here that
// leaves the fields as they were with no explanation — that is the exact shape
// this control was written to avoid.
export default class extends Controller {
  static values = { sessionId: String }

  static targets = [
    "status",
    "targetMarket", "problemsSolved", "bestCustomers"
  ]

  // Called from the container's data-action when step one has read the website.
  // The status line lives on THIS panel, beside the fields, because that is
  // where the person is looking when they arrive — the step-one status line
  // they may never have seen.
  receiveSuggestions(event) {
    const { suggestions = {}, website = "" } = event.detail || {}
    const filled = this.applySuggestions(suggestions)

    if (filled.length === 0) {
      // A read that filled nothing here is still a failure to the person in
      // front of it, and it says so rather than reporting a silent success.
      this.setStatus("Nothing on that page fitted these fields. Fill them in below.", "error")
      return
    }

    const from = website ? ` from ${website}` : ""
    this.setStatus(
      `Suggested${from} — ${filled.join(", ")} filled in below. Check and edit before continuing.`,
      "ok"
    )
  }

  // The read is under way on step one and this panel is one click away, so it
  // says so here too rather than leaving three empty boxes and no reason.
  receiveStarted() {
    this.setStatus("Reading your website to suggest these answers…", "working")
  }

  receiveFailed(event) {
    const message = (event.detail && event.detail.message) || ""
    this.setStatus(
      message || "We couldn't read your website. Fill these in below instead.",
      "error"
    )
  }

  applySuggestions(suggestions) {
    const filled = []

    if (this.assign(this.hasTargetMarketTarget, this.targetMarketTarget, suggestions.target_market)) {
      filled.push("your offer")
    }
    if (this.assign(this.hasProblemsSolvedTarget, this.problemsSolvedTarget, suggestions.problems_solved)) {
      filled.push("the problems you solve")
    }
    if (this.assign(this.hasBestCustomersTarget, this.bestCustomersTarget, suggestions.best_customers)) {
      filled.push("customers")
    }

    return filled
  }

  // Never overwrite something the person has already written. A suggestion is
  // worth less than a sentence they typed themselves, and silently replacing
  // one with the other is the same class of defect as writing to the record.
  assign(hasTarget, target, value) {
    if (!hasTarget) return false
    if (!value || !value.trim()) return false
    if (target.value.trim()) return false

    target.value = value
    target.dataset.intakeSuggested = "true"
    target.classList.add("ring-2", "ring-k3c-coral/40")
    target.addEventListener("input", () => this.clearSuggestedMark(target), { once: true })
    return true
  }

  clearSuggestedMark(target) {
    delete target.dataset.intakeSuggested
    target.classList.remove("ring-2", "ring-k3c-coral/40")
  }

  setStatus(message, kind) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.remove("text-red-500", "text-green-600", "text-theme-muted", "text-theme-primary")
    if (kind === "error") {
      this.statusTarget.classList.add("text-red-500")
    } else if (kind === "ok") {
      this.statusTarget.classList.add("text-green-600")
    } else if (kind === "working") {
      this.statusTarget.classList.add("text-theme-primary")
    } else {
      this.statusTarget.classList.add("text-theme-muted")
    }
  }
}
